import { css } from '@styled-system/css';
import { stack } from '@styled-system/patterns';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnalysisHeaderData } from '@/features/analysisPage/types';
import { Button } from '@/shared/components/ui/Button';
import { Logo } from '@/shared/components/ui/Logo';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { formatDate } from '@/shared/utils/format';

import { RenameAnalysisModal } from './RenameAnalysisModal';

interface AnalysisPageHeaderProps {
  analysis: AnalysisHeaderData;
  handleRename?: (newName: string) => Promise<void>;
  handleDelete?: () => void;
  isUpdating?: boolean;
  onBackToMenu?: () => void;
}

export const AnalysisPageHeader: FC<AnalysisPageHeaderProps> = ({
  analysis,
  handleRename,
  handleDelete,
  isUpdating = false,
  onBackToMenu,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const containerStyles = stack({
    direction: 'column',
    gap: '0.5rem',
    w: '100%',
  });

  const backButtonContainerStyles = css({
    position: 'fixed',
    top: '0.75rem',
    left: '1rem',
    zIndex: 1,
  });

  const headerStyles = stack({
    direction: 'row',
    alignItems: 'flex-start',
    gap: '1rem',
    p: '1rem',
    bg: 'transparent',
    w: '100%',
  });

  const headerContentStyles = stack({
    direction: 'column',
    gap: '0.25rem',
  });

  const headerTopContentStyles = stack({
    direction: 'row',
    gap: '0.94rem',
    alignItems: 'center',
  });

  const imageStyles = css({
    w: '3.125rem', // 50px
    h: '3.125rem', // 50px
    borderRadius: 'sm', // 6px border radius selon design
    flexShrink: 0,
    objectFit: 'cover',
    bg: 'base.segBg', // Background par défaut si pas d'image
    fontSize: 0,
  });

  const titleLinkStyles = css({
    display: 'flex',
    alignItems: 'center',
    mt: '-0.25rem',
    gap: '0.5rem',
    fontSize: isMobile ? 'md' : 'xl',
    fontWeight: 600, // Manrope SemiBold
    fontFamily: 'sans',
    color: 'font.primary', // #e6e6e9
    textDecoration: 'underline',
    textDecorationColor: 'font.lowkey',
    transition: 'color 150ms ease',
    _hover: {
      color: 'font.secondary', // Slightly different on hover
    },
  });

  const externalLinkIconStyles = css({
    w: '0.9375rem', // 15px
    h: '0.9375rem', // 15px
    ml: '-0.4rem',
  });

  const actionsContainerStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem', // 8px gap entre boutons
    flexShrink: 0,
  });

  const analysisDateStyles = css({
    fontSize: '2xs',
    color: 'font.lowkey',
    fontWeight: 600,
  });

  return (
    <div className={containerStyles}>
      {/* Back to Menu Button - Mobile only */}
      {isMobile && onBackToMenu && (
        <div className={backButtonContainerStyles}>
          <Button handleClick={onBackToMenu} icon="back" className={css({ bg: 'base.bg !important' })}>
            {t('analysisDetail.backToMenu')}
          </Button>
        </div>
      )}

      <div className={headerStyles}>
        {/* Product Image */}
        {analysis.imageUrl ? (
          <img src={analysis.imageUrl} alt={analysis.name} className={imageStyles} />
        ) : (
          <div className={imageStyles} />
        )}

        <div className={headerContentStyles}>
          <div className={headerTopContentStyles}>
            {/* Title with External Link */}
            <a href={analysis.pageUrl} target="_blank" rel="noopener noreferrer" className={titleLinkStyles}>
              <span>{analysis.name}</span>
              <Logo variant="external-link" color="font.primary" className={externalLinkIconStyles} />
            </a>

          </div>

          <p
            className={analysisDateStyles}
          >{t('analysisDetail.createdOn', { date: formatDate(analysis.createdAt) })}</p>
        </div>

        <div className={css({ flexGrow: 1 })} />

        {/* Action Buttons - Masqués pour les analyses publiques ou sur mobile */}
        {!(analysis.isPublic || isMobile) && (
          <div className={actionsContainerStyles}>
            {handleRename && (
              <Button handleClick={() => setIsRenameModalOpen(true)} icon="rename" disabled={isUpdating}>
                {t('analysisDetail.rename')}
              </Button>
            )}
            {handleDelete && (
              <Button handleClick={handleDelete} icon="trash" color="accent.red" disabled={isUpdating}>
                {t('analysisDetail.delete')}
              </Button>
            )}
          </div>
        )}

        {/* Rename Modal - Masqué pour les analyses publiques */}
        {handleRename && !analysis.isPublic && (
          <RenameAnalysisModal
            isOpen={isRenameModalOpen}
            onClose={() => setIsRenameModalOpen(false)}
            currentName={analysis.name}
            onRename={handleRename}
            isRenaming={isUpdating}
          />
        )}
      </div>
    </div>
  );
};
